import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchCustomersRequest,
  fetchCustomersSuccess,
  fetchCustomersFailure,
} from "../../actions/customer";
import { fetchCustomers } from "../../api/customer";
import "../../assets/css/reporting.css";
import { Button, Form } from "react-bootstrap";
import { fetchProcessedBodies } from "../../api/processedBodies";
import classNames from "classnames";
import CustomAlert from "../general/customAlert";
import moment from "moment";
import DatePicker from "react-datepicker";
import CustomTable from "../general/CustomTable";
import { CUSTOMER_REPORT_PDF } from "../../constants";
import { fetchOrigins } from "../../api/origin";
import {
  fetchOriginsFailure,
  fetchOriginsRequest,
  fetchOriginsSuccess,
} from "../../actions/origin";
import CustomerReportPDF from "./reportPDF";
import { useCompany } from "../../helpers/CompanyContext";
import api from "../../helpers/util";

const SPECIES_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "Bovine", label: "Bovine" },
  { value: "Ovine", label: "Ovine" },
  { value: "Caprine", label: "Caprine" },
  { value: "Porcine", label: "Porcine" },
  { value: "Others", label: "Others" },
];

const NAMED_SPECIES = ["bovine", "ovine", "caprine", "porcine"];

const ReportingPage = () => {
  const { selectedCompany, companies } = useCompany();
  const pdfContainerRef = useRef(null);

  const [selectedCustomer, setSelectedCustomer] = useState("ALL");
  const [selectedOrigin, setSelectedOrigin] = useState("ALL");
  const [selectedSpecies, setSelectedSpecies] = useState("ALL");

  const [isCustomerEnabled, setIsCustomerEnabled] = useState(false);
  const [isOriginEnabled, setIsOriginEnabled] = useState(false);

  const [processedBodies, setProcessedBodies] = useState([]);
  const [filteredBodies, setFilteredBodies] = useState([]);

  const { customers, loading, error } = useSelector((state) => state.customers);
  const {
    origins,
    loading: loadingOrigins,
    error: errorOrigins,
  } = useSelector((state) => state.origins);

  const [isSendingEmails, setSendingEmails] = useState(false);
  const [sendResults, setSendResults] = useState([]);
  const dispatch = useDispatch();
  const [isMsg, setIsMsg] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState();
  const [isGenerated, setIsGenerated] = useState(false);

  const [rowsPerPage, setRowsPerPage] = useState(20);

  const [visibleColumns, setVisibleColumns] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const sortingColumns = ["body_number", "hscw", "fat_score"];

  const [bounceWarnings, setBounceWarnings] = useState([]);
  const [showBounceModal, setShowBounceModal] = useState(false);
  const [pendingSend, setPendingSend] = useState(false);

  const [loadingBodies, setLoadingBodies] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));

  const isInitialMount = useRef(true);

  const BVM_COMPANY_ID = 2;

  // ── Sorting ───────────────────────────────────────────────────────────────
  const handleSort = useCallback(
    (col) => {
      let direction = sortConfig.direction;
      let column = col;
      if (column) {
        if (sortConfig.key === column && sortConfig.direction === "asc") {
          direction = "desc";
        } else if (sortConfig.key === column && sortConfig.direction === "desc") {
          direction = "asc";
        }
        setSortConfig({ key: direction ? column : null, direction });
      } else {
        column = sortConfig.key;
      }

      setFilteredBodies((prev) =>
        [...prev].sort((a, b) => {
          if (a[column] < b[column]) return direction === "asc" ? -1 : 1;
          if (a[column] > b[column]) return direction === "asc" ? 1 : -1;
          return 0;
        })
      );
    },
    [sortConfig]
  );

  const UseSetFilteredBodies = useCallback(
    (data) => {
      if (sortConfig.key) {
        setFilteredBodies(
          [...data].sort((a, b) => {
            if (a[sortConfig.key] < b[sortConfig.key])
              return sortConfig.direction === "asc" ? -1 : 1;
            if (a[sortConfig.key] > b[sortConfig.key])
              return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
          })
        );
      } else {
        setFilteredBodies([...data]);
      }
      setTotalPages(
        rowsPerPage === "ALL" ? 1 : Math.ceil(data.length / rowsPerPage)
      );
    },
    [sortConfig, rowsPerPage]
  );

  // ── Filters ───────────────────────────────────────────────────────────────
  const applyFilters = useCallback(
    (bodies, custEnabled, customer, origEnabled, origin, species) => {
      let result = bodies;

      if (custEnabled && customer !== "ALL") {
        result = result.filter((b) => b.owner === customer.name);
      }

      if (origEnabled && origin !== "ALL") {
        result = result.filter((b) => b.origin === origin.name);
      }

      if (species !== "ALL") {
        if (species === "Others") {
          result = result.filter(
            (b) => !NAMED_SPECIES.includes((b.species || "").toLowerCase())
          );
        } else {
          result = result.filter(
            (b) => (b.species || "").toLowerCase() === species.toLowerCase()
          );
        }
      }

      UseSetFilteredBodies(result);
      setCurrentPage(1);
    },
    [UseSetFilteredBodies]
  );

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchCustomersFunc = useCallback(async () => {
    dispatch(fetchCustomersRequest());
    try {
      const data = await fetchCustomers();
      dispatch(fetchCustomersSuccess(data));
    } catch (err) {
      dispatch(fetchCustomersFailure(err.message));
    }
  }, [dispatch]);

  const fetchOriginsFunc = useCallback(async () => {
    dispatch(fetchOriginsRequest());
    try {
      const data = await fetchOrigins();
      dispatch(fetchOriginsSuccess(data));
    } catch (err) {
      dispatch(fetchOriginsFailure(err.message));
    }
  }, [dispatch]);

  const fetchBodies = useCallback(
    async (date) => {
      setLoadingBodies(true);
      try {
        const data = await fetchProcessedBodies(date);
        const bodies = data.map((d) => ({
          ...d,
          fat_score: d.p2fat
            ? parseFloat(d.p2fat).toFixed(2)
            : d.fat_score ?? "0.00",
        }));
        setProcessedBodies(bodies);
        applyFilters(
          bodies,
          isCustomerEnabled,
          selectedCustomer,
          isOriginEnabled,
          selectedOrigin,
          selectedSpecies
        );
        if (bodies.length > 0 && visibleColumns.length < 1) {
          setVisibleColumns(
            CUSTOMER_REPORT_PDF.reduce((acc, item) => {
              acc[item.name] = true;
              return acc;
            }, {})
          );
        }
      } catch (err) {
        console.error("Error fetching processedBodies:", err);
      } finally {
        setLoadingBodies(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCustomerEnabled, selectedCustomer, isOriginEnabled, selectedOrigin, selectedSpecies, visibleColumns]
  );

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchCustomersFunc();
    fetchOriginsFunc();
  }, [fetchCustomersFunc, fetchOriginsFunc]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (selectedCompany) {
      setSelectedCustomer("ALL");
      setSelectedOrigin("ALL");
      setSelectedSpecies("ALL");
      setIsCustomerEnabled(false);
      setIsOriginEnabled(false);
      setIsGenerated(false);
      fetchCustomersFunc();
      fetchOriginsFunc();
      fetchBodies(selectedDate);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedDate && selectedCompany) {
      fetchBodies(selectedDate);
    }
  }, [selectedDate, selectedCompany]);

  useEffect(() => {
    handleSort(null);
  }, [handleSort]);

  // Recompute pagination whenever the page size changes
  useEffect(() => {
    setTotalPages(
      rowsPerPage === "ALL" ? 1 : Math.ceil(filteredBodies.length / rowsPerPage)
    );
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const startIndex = rowsPerPage === "ALL" ? 0 : (currentPage - 1) * rowsPerPage;
  const endIndex =
    rowsPerPage === "ALL" ? filteredBodies.length : startIndex + rowsPerPage;

  const handlePageChange = (page) => setCurrentPage(page);

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value === "ALL" ? "ALL" : Number(e.target.value);
    setRowsPerPage(value);
  };

  const handleDateChange = (date) => {
    if (date === null) setSelectedDate(null);
    if (date) setSelectedDate(moment(date).format("YYYY-MM-DD"));
  };

  const getColumns = () => [
    { name: "#", value: "index" },
    ...CUSTOMER_REPORT_PDF.filter((obj) => visibleColumns[obj.name]),
  ];

  // Resolve every unique recipient email for the pre-send bounce check.
  // Matches the recipient resolution used in reportPDF.js's multipleRecipients:
  // legacy customer.emailaddress / origin.email PLUS any contacts flagged
  // includeinemail = 1 on the matched customer/origin.
  const getRecipientEmails = () => {
    const all = new Set();

    filteredBodies.forEach((b) => {
      const entity = isOriginEnabled
        ? origins.find((o) => o.name.trim() === b.origin?.trim())
        : customers.find((c) => c.name.trim() === b.owner?.trim());

      if (!entity) return;

      (entity.contacts || [])
        .filter((c) => c.includeinemail && c.email)
        .forEach((c) => all.add(c.email.trim().toLowerCase()));

      const legacy = (entity.emailaddress || entity.email || "").trim().toLowerCase();
      if (legacy) all.add(legacy);
    });

    return [...all];
  };

  // ── Send logic ─────────────────────────────────────────────────────────────
  const doSendReports = () => {
    CustomerReportPDF({
      orders: filteredBodies,
      columns: getColumns(),
      isCustomerEnabled,
      isOriginEnabled,        // ← pass origin mode
      filename: "customer_report.pdf",
      send: true,
      setSendingEmails,
      setMsg,
      setIsMsg,
      customers,
      origins,
      setErr,
      setSendResults,
      selectedCompany,
      companies,
    });
    pdfContainerRef.current.style.display = "none";
    setIsGenerated(false);
    setShowBounceModal(false);
    setPendingSend(false);
    setBounceWarnings([]);
  };

  const handleSendClick = async () => {
    setSendResults([]);
    const recipientEmails = getRecipientEmails();

    try {
      const { data } = await api.post("/email/bounce-check", {
        recipients: recipientEmails,
      });

      if (data.bounced?.length > 0) {
        setBounceWarnings(data.bounced);
        setShowBounceModal(true);
        setPendingSend(true);
        return;
      }
    } catch (e) {
      console.warn("Bounce check failed, proceeding anyway:", e);
    }

    doSendReports();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="reporting-page">
      <h4 className="mb-3 text-center text-acumen-color">Reporting</h4>

      {loading && <div className="alert alert-info">Loading customers...</div>}
      {error && <div className="alert alert-danger">Error: {error}</div>}
      {loadingOrigins && <div className="alert alert-info">Loading origins...</div>}
      {errorOrigins && <div className="alert alert-danger">Error: {errorOrigins}</div>}

      <div className="container-fluid">
        <div className="d-flex flex-column flex-md-row">
          <div className="col-12 col-md-9 mb-1 mb-md-0 d-flex flex-wrap p-0 gap-4">

            {/* ── Customer ── */}
            <Form.Group className="m-0 p-0" style={{ minWidth: "200px", flex: "0 1 200px" }}>
              <div className="d-flex align-items-center mb-1">
                <input
                  type="checkbox"
                  id="chk-customer"
                  checked={isCustomerEnabled}
                  onChange={() => {
                    const next = !isCustomerEnabled;
                    setIsCustomerEnabled(next);
                    applyFilters(processedBodies, next, selectedCustomer, isOriginEnabled, selectedOrigin, selectedSpecies);
                  }}
                  disabled={isGenerated || isSendingEmails}
                  className="me-1"
                />
                <Form.Label htmlFor="chk-customer" className="m-0">Select Customer</Form.Label>
              </div>
              <select
                className="form-select m-0 w-100"
                disabled={!isCustomerEnabled || isGenerated || isSendingEmails}
                value={selectedCustomer === "ALL" ? "ALL" : selectedCustomer.id}
                onChange={(e) => {
                  const customer =
                    e.target.value === "ALL"
                      ? "ALL"
                      : customers.find((c) => c.id === Number(e.target.value));
                  setSelectedCustomer(customer);
                  applyFilters(processedBodies, isCustomerEnabled, customer, isOriginEnabled, selectedOrigin, selectedSpecies);
                }}>
                <option value="ALL">All</option>
                {customers
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
              </select>
            </Form.Group>

            {/* ── Origin ── */}
            <Form.Group className="m-0 p-0" style={{ minWidth: "200px", flex: "0 1 200px" }}>
              <div className="d-flex align-items-center mb-1">
                <input
                  type="checkbox"
                  id="chk-origin"
                  checked={isOriginEnabled}
                  onChange={() => {
                    const next = !isOriginEnabled;
                    setIsOriginEnabled(next);
                    applyFilters(processedBodies, isCustomerEnabled, selectedCustomer, next, selectedOrigin, selectedSpecies);
                  }}
                  disabled={isGenerated || isSendingEmails}
                  className="me-1"
                />
                <Form.Label htmlFor="chk-origin" className="m-0">Select Origin</Form.Label>
              </div>
              <select
                className="form-select m-0 w-100"
                disabled={!isOriginEnabled || isGenerated || isSendingEmails}
                value={selectedOrigin === "ALL" ? "ALL" : selectedOrigin.id}
                onChange={(e) => {
                  const origin =
                    e.target.value === "ALL"
                      ? "ALL"
                      : origins.find((c) => c.id === Number(e.target.value));
                  setSelectedOrigin(origin);
                  applyFilters(processedBodies, isCustomerEnabled, selectedCustomer, isOriginEnabled, origin, selectedSpecies);
                }}>
                <option value="ALL">All</option>
                {origins
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((origin) => (
                    <option key={origin.id} value={origin.id}>{origin.name}</option>
                  ))}
              </select>
            </Form.Group>

            {/* ── Species ── */}
            <Form.Group className="m-0 p-0" style={{ minWidth: "140px", flex: "0 1 140px" }}>
              <Form.Label className="m-0 mb-1">Select Species</Form.Label>
              <select
                className="form-select m-0 w-100"
                disabled={isGenerated || isSendingEmails}
                value={selectedSpecies}
                onChange={(e) => {
                  const species = e.target.value;
                  setSelectedSpecies(species);
                  applyFilters(processedBodies, isCustomerEnabled, selectedCustomer, isOriginEnabled, selectedOrigin, species);
                }}>
{SPECIES_OPTIONS.filter((opt) => {
  if (["Caprine", "Others"].includes(opt.label)) {
    return selectedCompany === BVM_COMPANY_ID;
  }
  return true;
}).map((opt) => (
  <option key={opt.value} value={opt.value}>{opt.label}</option>
))}
              </select>
            </Form.Group>

{/* ── Date ── */}
<Form.Group
  className="m-0 p-0 d-flex flex-column date-picker"
  style={{ width: "150px", flex: "0 1 150px"}}
>
  <Form.Label className="m-0 mb-1">Select Date</Form.Label>
  <DatePicker
    selected={selectedDate ? new Date(selectedDate) : null}
    onChange={handleDateChange}
    dateFormat="yyyy/MM/dd"
    className="form-control w-100"
    id="production_date"
    name="production_date"
    disabled={isGenerated || isSendingEmails}
    onKeyDown={(e) => e.preventDefault()}
    isClearable={true}
    maxDate={new Date()}
  />
</Form.Group>

{/* ── Rows per page ── */}
<Form.Group
  className="m-0 p-0 d-flex flex-column"
  style={{ minWidth: "120px", flex: "0 1 120px" }}
>
  <Form.Label className="m-0 mb-1">Show Entries</Form.Label>
  <select
    className="form-select m-0 w-100"
    disabled={isGenerated || isSendingEmails}
    value={rowsPerPage}
    onChange={handleRowsPerPageChange}
  >
    <option value={20}>20</option>
    <option value={50}>50</option>
    <option value="ALL">All</option>
  </select>
</Form.Group>
          </div>

          {/* ── Action buttons ── */}
          <div className="col-12 col-md-3 d-flex justify-content-md-end mt-3 mt-md-0 align-items-center">
            <Button
              className="btn btn-acumen-color me-2"
              onClick={() => {
                setIsGenerated(!isGenerated);
                if (!isGenerated) {
                  CustomerReportPDF({
                    orders: filteredBodies,
                    columns: getColumns(),
                    isCustomerEnabled,
                    isOriginEnabled,
                    filename: "customer_report.pdf",
                    pdfContainerRef: pdfContainerRef,
                    selectedCompany,
                    companies,
                  });
                } else {
                  pdfContainerRef.current.style.display = "none";
                }
              }}>
              {isGenerated ? "Cancel" : "Generate Reports"}
            </Button>

            {isGenerated && (
              <Button
                className={classNames("btn btn-acumen-color", { disabled: isSendingEmails })}
                onClick={handleSendClick}>
                {isSendingEmails ? "Sending..." : "Send Reports"}
              </Button>
            )}
          </div>
        </div>

        {/* ── PDF preview container ── */}
        <div
          ref={pdfContainerRef}
          style={{
            width: "100%",
            height: "600px",
            border: "1px solid #ccc",
            marginTop: "10px",
            display: isGenerated ? "flex" : "none",
          }}
        />

        {/* ── Data table ── */}
        {!isGenerated && (
          <div className="mt-4">
            {loadingBodies ? (
              <div className="alert alert-info">Loading data...</div>
            ) : filteredBodies.length > 0 ? (
              <>
                <CustomTable
                  items={filteredBodies.slice(startIndex, endIndex)}
                  columns={CUSTOMER_REPORT_PDF}
                  visibleColumns={visibleColumns}
                  setVisibleColumns={setVisibleColumns}
                  cantHideColumns={["hscw"]}
                  maxHeight="calc(100vh - 320px)"
                  handleSort={handleSort}
                  sortConfig={sortConfig}
                  sortingColumns={sortingColumns}
                />
                {rowsPerPage !== "ALL" && totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="btn btn-acumen-color text-white me-2"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}>
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button
                        key={index + 1}
                        className={`btn ${currentPage === index + 1 ? "btn-secondary" : "btn-light"} me-1`}
                        onClick={() => handlePageChange(index + 1)}>
                        {index + 1}
                      </button>
                    ))}
                    <button
                      className="btn btn-acumen-color text-white ms-2"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}>
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="alert alert-warning mt-2">No data available.</div>
            )}
          </div>
        )}

        {/* ── Send results table ── */}
        {sendResults.length > 0 && (
          <div className="mt-3 p-3 border rounded" style={{ background: "#f8f9fa" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Email Send Results</strong>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setSendResults([])}>
                Dismiss
              </button>
            </div>
            <table className="table table-sm table-bordered mb-0">
              <thead style={{ background: "#00b894", color: "white" }}>
                <tr>
                  <th>#</th>
                  <th>Recipient</th>
                  <th>File</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sendResults.map((r, i) => (
                  <tr key={i}>
                    <td>{r.index}</td>
                    <td>{r.intendedRecipient}</td>
                    <td style={{ fontSize: "0.8em", color: "#666" }}>{r.filename}</td>
                    <td>
                      <span style={{ color: r.resendId ? "#00b894" : "#dc3545", fontWeight: "bold" }}>
                        {r.resendId ? "✅ Sent" : "❌ Failed"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Toast alert ── */}
      {isMsg && (
        <CustomAlert
          isError={err}
          message={msg}
          showMessage={isMsg}
          setShowMessage={setIsMsg}
        />
      )}

      {/* ── Bounce warning modal ── */}
      {showBounceModal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
          <div
            style={{
              background: "white", borderRadius: 8, padding: 24,
              maxWidth: 560, width: "90%",
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            }}>
            <h5 style={{ color: "#dc3545", marginBottom: 12 }}>
              ⚠️ Previously Bounced Addresses
            </h5>
            <p style={{ fontSize: "0.9em", color: "#555", marginBottom: 12 }}>
              The following recipients have bounced emails in the past.
              Sending to them again may fail. Do you want to proceed anyway?
            </p>
            <table className="table table-sm table-bordered mb-3">
              <thead style={{ background: "#dc3545", color: "white" }}>
                <tr>
                  <th>Email</th>
                  <th>Bounce Reason</th>
                  <th>Last Bounced</th>
                </tr>
              </thead>
              <tbody>
                {bounceWarnings.map((b, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: "0.85em" }}>{b.recipient}</td>
                    <td style={{ fontSize: "0.85em", color: "#dc3545" }}>
                      {b.bounce_reason || "Unknown"}
                    </td>
                    <td style={{ fontSize: "0.85em" }}>
                      {moment(b.last_bounced_at).format("YYYY-MM-DD")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="d-flex gap-2 justify-content-end">
              <button
                className="btn btn-outline-secondary"
                onClick={() => {
                  setShowBounceModal(false);
                  setPendingSend(false);
                  setBounceWarnings([]);
                }}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={doSendReports}>
                Send Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportingPage;